import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  Text,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useFormikContext } from "formik";
import _ from "lodash";
import React, { useCallback, useState } from "react";
import { EmptyState } from "src/components/EmptyState";
import { ColorInput } from "src/components/Inputs/ColorInput";
import { UploadFileInput } from "src/components/Inputs/UploadFileInput";
import { useOrgConfigKey } from "src/generated/hooks/org-config/queries";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useOrganization } from "src/hooks/useOrganization";
import LaunchAppSvg from "src/images/launch-app.svg?react";
import { Env, useEnv } from "src/services/env";
import * as OrgService from "src/services/orgService";
import { sleep } from "src/services/sleep";
import * as RD from "src/types/remoteData";
import { z } from "zod";
import { BrandingPreview } from "./BrandingPreview";
import * as ColorPalette from "./ColorPalette";
import * as Logo from "./Logo";

export const Form = () => {
  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Replace logo",
    body: "",
    confirmButton: { label: "Upload and replace" },
    cancelButton: { label: "Cancel" },
  });
  const formik = useFormikContext<FormikType>();
  const [invalidColorLogo, setInvalidColorLogo] = useState<
    string | undefined
  >();
  const [invalidWhiteLogo, setInvalidWhiteLogo] = useState<
    string | undefined
  >();

  const confirmOverwriteExistingLogo = async (logo: keyof Logos) => {
    const logoType =
      logo === "colorLogoPng" || logo === "colorLogoSvg"
        ? "Black or Fullcolor"
        : "White";
    const imageType =
      logo === "colorLogoPng" || logo === "whiteLogoPng" ? "PNG" : "SVG";
    return await confirm({
      body: `You’ve already uploaded a ${logoType} logo ${imageType} file. Would you like to replace it?`,
    });
  };

  const updateFormikFieldLogo = async (logoKey: keyof Logos, file: File) => {
    const logo = formik.values.branding[logoKey];
    if (logo?.type === "existing") {
      if (!(await confirmOverwriteExistingLogo(logoKey))) {
        return;
      }
    }

    let imageType: "png" | "svg";
    switch (logoKey) {
      case "colorLogoPng":
      case "whiteLogoPng":
        imageType = "png";
        break;
      case "colorLogoSvg":
      case "whiteLogoSvg":
        imageType = "svg";
        break;
    }

    const objectUrl = URL.createObjectURL(file);
    formik.setFieldValue(`branding.${logoKey}`, {
      type: "new",
      objectUrl,
      imageType,
      name: file.name,
    } satisfies Logo.NewLogo);
  };

  const handleLogoUpload = async (
    colorType: "color" | "white",
    files: FileList
  ): Promise<void> => {
    if (colorType === "color") {
      setInvalidColorLogo(undefined);
      Array.from(files).forEach(async (file) => {
        if (file.type === "image/png") {
          await updateFormikFieldLogo("colorLogoPng", file);
        } else if (file.type === "image/svg+xml") {
          await updateFormikFieldLogo("colorLogoSvg", file);
        } else {
          setInvalidColorLogo(file.name);
        }
      });
    } else if (colorType === "white") {
      setInvalidWhiteLogo(undefined);
      Array.from(files).forEach(async (file) => {
        if (file.type === "image/png") {
          await updateFormikFieldLogo("whiteLogoPng", file);
        } else if (file.type === "image/svg+xml") {
          await updateFormikFieldLogo("whiteLogoSvg", file);
        } else {
          setInvalidWhiteLogo(file.name);
        }
      });
    }
  };

  const previewBackgroundColor = _.isEmpty(formik.values.branding.primaryColor)
    ? OrgConfig.BrandingConfigSchema.default.colors.primary["500"]
    : formik.values.branding.primaryColor;

  const [showPreview, setShowPreview] = useState(false);

  const deleteLogo = async (formikFieldName: keyof Logos) => {
    const logo = formik.values.branding[formikFieldName];
    if (logo?.type === "existing") {
      let body: string;
      switch (formikFieldName) {
        case "colorLogoPng":
          body = `Are you sure you want to remove Black or Fullcolor PNG logo?`;
          break;
        case "colorLogoSvg":
          body = `Are you sure you want to remove Black or Fullcolor SVG logo?`;
          break;
        case "whiteLogoPng":
          body = `Are you sure you want to remove White PNG logo?`;
          break;
        case "whiteLogoSvg":
          body = `Are you sure you want to remove White SVG logo?`;
          break;
      }
      if (
        !(await confirm({
          body,
          header: "Remove logo",
          confirmButton: { label: "Remove logo" },
        }))
      ) {
        return;
      }
    }

    formik.setFieldValue(
      `branding.${formikFieldName}`,
      getDefaultLogo(formikFieldName)
    );
  };

  return (
    <>
      <Flex gap={4}>
        <Flex direction="column" gap={2} flex="1">
          <Heading as="h3" variant="admin">
            Logos
          </Heading>
          <Flex direction="column" gap={2} alignItems="flex-start">
            <Text fontSize="md" fontWeight="500">
              Black or full color logo
            </Text>
            <Text color="blackAlpha.700" fontSize="sm">
              Upload a logo that can be used over light or white backgrounds
            </Text>
            {formik.values.branding.colorLogoPng && (
              <Logo.LogoPreview
                logo={formik.values.branding.colorLogoPng}
                onDelete={() => deleteLogo("colorLogoPng")}
              />
            )}
            {formik.values.branding.colorLogoSvg && (
              <Logo.LogoPreview
                logo={formik.values.branding.colorLogoSvg}
                onDelete={() => {
                  deleteLogo("colorLogoSvg");
                }}
              />
            )}
            {invalidColorLogo && (
              <Logo.InvalidLogo
                name={invalidColorLogo}
                onClose={() => setInvalidColorLogo(undefined)}
              />
            )}
            <UploadFileInput
              boxProps={{ marginY: "0" }}
              buttonLabel="Upload files"
              accept="image/png, image/svg+xml"
              multiple={true}
              onUpload={(files) => handleLogoUpload("color", files)}
            />
            <Text color="blackAlpha.700" fontSize="xs">
              Upload a PNG and an SVG version of the logo, with no background
            </Text>
          </Flex>
          <Flex direction="column" gap={2} alignItems="flex-start">
            <Text fontSize="md" fontWeight="500">
              Whitescale logo
            </Text>
            <Text color="blackAlpha.700" fontSize="sm">
              Upload a logo that can be used over dark or primary color
              backgrounds
            </Text>
            {formik.values.branding.whiteLogoPng && (
              <Logo.LogoPreview
                logo={formik.values.branding.whiteLogoPng}
                onDelete={() => deleteLogo("whiteLogoPng")}
                previewPopoverProps={{
                  background: previewBackgroundColor,
                }}
              />
            )}
            {formik.values.branding.whiteLogoSvg && (
              <Logo.LogoPreview
                logo={formik.values.branding.whiteLogoSvg}
                onDelete={() => deleteLogo("whiteLogoSvg")}
                previewPopoverProps={{ background: previewBackgroundColor }}
              />
            )}
            {invalidWhiteLogo && (
              <Logo.InvalidLogo
                name={invalidWhiteLogo}
                onClose={() => setInvalidWhiteLogo(undefined)}
              />
            )}
            <UploadFileInput
              boxProps={{ marginY: "0" }}
              buttonLabel="Upload files"
              accept="image/png, image/svg+xml"
              multiple
              onUpload={(files) => handleLogoUpload("white", files)}
            />
            <Text color="blackAlpha.700" fontSize="xs">
              Upload a PNG and an SVG version of the logo, with no background
            </Text>
          </Flex>
          <Heading as="h3" variant="admin">
            Brand
          </Heading>
          <Flex direction="column" gap={2} alignItems="flex-start">
            <Text fontSize="md" fontWeight="500">
              Primary color
            </Text>
            <Text color="blackAlpha.700" fontSize="sm">
              Enter the brand's primary color HEX code
            </Text>
            <FormControl
              direction="column"
              alignItems="flex-start"
              as={Flex}
              isInvalid={formik.errors.branding?.primaryColor !== undefined}
            >
              <ColorInput
                name="branding.primaryColor"
                onChange={(value, field) => {
                  formik.setFieldValue("branding.disabled", false);
                  formik.setFieldValue(field.name, value);
                }}
              />
              <FormErrorMessage>
                {formik.errors.branding?.primaryColor}
              </FormErrorMessage>
            </FormControl>
            <ColorPalette.ColorPalette
              color={formik.values.branding.primaryColor}
            />
          </Flex>
        </Flex>
        <Flex
          direction="column"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          flex="1"
          alignItems="center"
          justifyContent="center"
          overflow="hidden"
          padding={showPreview ? 0 : 4}
        >
          {showPreview ? (
            <BrandingPreview branding={formik.values.branding} />
          ) : (
            <Flex direction="column" gap={4} alignItems="center">
              <EmptyState
                description="Add logos and a primary color to preview your Brand"
                Svg={LaunchAppSvg}
              />
              <Button
                onClick={() => setShowPreview(true)}
                variant="outline"
                colorScheme="gray"
              >
                Preview
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>
      {confirmationDialog}
    </>
  );
};

function getInitialValues(
  branding: OrgConfig.BrandingConfig | null
): FormikType {
  if (branding) {
    return {
      branding: {
        primaryColor: branding.colors.primary[500],
        colorLogoSvg: toLogo(branding, "colorSvg"),
        colorLogoPng: toLogo(branding, "colorPng"),
        whiteLogoSvg: toLogo(branding, "whiteSvg"),
        whiteLogoPng: toLogo(branding, "whitePng"),
      },
    };
  }

  // Branding doesn't exist, use default branding
  return {
    branding: {
      primaryColor: OrgConfig.BrandingConfigSchema.default.colors.primary[500],
      colorLogoPng: getDefaultLogo("colorLogoPng"),
      colorLogoSvg: getDefaultLogo("colorLogoSvg"),
      whiteLogoPng: getDefaultLogo("whiteLogoPng"),
      whiteLogoSvg: getDefaultLogo("whiteLogoSvg"),
    },
  };
}

function getLogoName(logoUrl: string): string {
  return logoUrl.split("/").pop() ?? logoUrl;
}

export async function updateBranding(args: {
  env: Env;
  formik: FormikType;
  config: OrgConfig.BrandingConfig | null;
  organization: { path: string; name: string };
  update: (config: OrgConfig.BrandingConfig) => Promise<void>;
}): Promise<void> {
  const { branding } = args.formik;
  const { env, organization } = args;
  const orgPath = organization.path;

  // Upload any new logos
  if (branding.colorLogoSvg?.type === "new")
    await uploadLogo({
      orgPath,
      logo: branding.colorLogoSvg,
    });

  if (branding.colorLogoPng?.type === "new")
    await uploadLogo({
      orgPath,
      logo: branding.colorLogoPng,
    });

  if (branding.whiteLogoSvg?.type === "new")
    await uploadLogo({
      orgPath,
      logo: branding.whiteLogoSvg,
    });

  if (branding.whiteLogoPng?.type === "new")
    await uploadLogo({
      orgPath,
      logo: branding.whiteLogoPng,
    });

  await args.update(toPayload(env, organization, args.formik, args.config));
}

/**
 * Helpers functions
 */

type UploadLogoArgs = {
  orgPath: string;
  logo: Logo.NewLogo;
};

async function uploadLogo({ orgPath, logo }: UploadLogoArgs): Promise<void> {
  const path = Logo.toPath(orgPath, logo);
  const url = await OrgService.generateUploadUrl(path);
  const file = await Logo.getFile(logo);
  const uploadResponse = await fetch(url, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": logo.imageType === "png" ? "image/png" : "image/svg+xml",
    },
  });

  if (!uploadResponse.ok) {
    throw new Error("Failed to upload logo");
  }
}

function toPayload(
  env: Env,
  organization: { name: string; path: string },
  formik: FormikType,
  config: OrgConfig.BrandingConfig | null
): OrgConfig.BrandingConfig {
  return {
    name: organization.name,
    message: {
      footer:
        config?.message.footer ??
        OrgConfig.BrandingConfigSchema.default.message.footer,
      replyTo:
        config?.message.replyTo ??
        OrgConfig.BrandingConfigSchema.default.message.replyTo,
    },
    colors: {
      primary: ColorPalette.generateColorPalette(formik.branding.primaryColor),
    },
    logos: {
      colorSvg: Logo.toUrl(
        env,
        organization.path,
        formik.branding.colorLogoSvg
      ),
      whiteSvg: Logo.toUrl(
        env,
        organization.path,
        formik.branding.whiteLogoSvg
      ),
      colorPng: Logo.toUrl(
        env,
        organization.path,
        formik.branding.colorLogoPng
      ),
      whitePng: Logo.toUrl(
        env,
        organization.path,
        formik.branding.whiteLogoPng
      ),
    },
  };
}

export function getDefaultLogo(type: keyof Logos): Logo.DefaultLogo {
  switch (type) {
    case "colorLogoPng":
      return {
        type: "default",
        imageType: "png",
        url: OrgConfig.BrandingConfigSchema.default.logos.colorPng,
      };
    case "colorLogoSvg":
      return {
        type: "default",
        imageType: "svg",
        url: OrgConfig.BrandingConfigSchema.default.logos.colorSvg,
      };
    case "whiteLogoPng":
      return {
        type: "default",
        imageType: "png",
        url: OrgConfig.BrandingConfigSchema.default.logos.whitePng,
      };
    case "whiteLogoSvg":
      return {
        type: "default",
        imageType: "svg",
        url: OrgConfig.BrandingConfigSchema.default.logos.whiteSvg,
      };
  }
}

export function toLogo(
  branding: OrgConfig.BrandingConfig,
  logo: keyof OrgConfig.BrandingConfigLogos
): Logo.DefaultLogo | Logo.ExistingLogo {
  switch (logo) {
    case "colorPng": {
      if (
        OrgConfig.BrandingConfigSchema.default.logos.colorPng ===
        branding.logos.colorPng
      ) {
        return getDefaultLogo("colorLogoPng");
      }
      return {
        type: "existing",
        imageType: "svg",
        name: getLogoName(branding.logos.colorPng),
        url: branding.logos.colorPng,
      };
    }
    case "colorSvg": {
      if (
        OrgConfig.BrandingConfigSchema.default.logos.colorSvg ===
        branding.logos.colorSvg
      ) {
        return getDefaultLogo("colorLogoSvg");
      }
      return {
        type: "existing",
        imageType: "svg",
        name: getLogoName(branding.logos.colorSvg),
        url: branding.logos.colorSvg,
      };
    }
    case "whitePng": {
      if (
        OrgConfig.BrandingConfigSchema.default.logos.whitePng ===
        branding.logos.whitePng
      ) {
        return getDefaultLogo("whiteLogoPng");
      }
      return {
        type: "existing",
        imageType: "png",
        name: getLogoName(branding.logos.whitePng),
        url: branding.logos.whitePng,
      };
    }
    case "whiteSvg": {
      if (
        OrgConfig.BrandingConfigSchema.default.logos.whiteSvg ===
        branding.logos.whiteSvg
      ) {
        return getDefaultLogo("whiteLogoSvg");
      }
      return {
        type: "existing",
        imageType: "svg",
        name: getLogoName(branding.logos.whiteSvg),
        url: branding.logos.whiteSvg,
      };
    }
  }
}

export function useForm(): {
  initialValues: RD.RemoteData<Error, FormikType>;
  submit: (values: FormikType) => Promise<void>;
} {
  const client = useQueryClient();
  const { remoteData, update } =
    OrgService.useOrgConfig<OrgConfig.BrandingConfig>("Branding");
  const orgRD = useOrganization();
  const env = useEnv();

  const initialValues: RD.RemoteData<Error, FormikType> = React.useMemo(() => {
    return remoteData.map((config): FormikType => {
      return getInitialValues(config);
    });
  }, [remoteData]);

  const submit = useCallback(
    async (values: FormikType) => {
      if (!orgRD.hasData() || !remoteData.hasData()) {
        throw new Error("Missing organization data");
      }

      await updateBranding({
        env,
        formik: values,
        organization: orgRD.data,
        config: remoteData.data,
        update,
      });
      await sleep(1000); // Need to put delay to wait for CDN invalidation to finish. This not not guaranteed to always work, but that's okay since this not a critical task.
      client.invalidateQueries({
        queryKey: [useOrgConfigKey, "Branding"],
        exact: false,
      });
    },
    [client, env, orgRD, remoteData, update]
  );

  return { initialValues, submit };
}

const LogosSchema = z.object({
  colorLogoPng: Logo.LogoSchema.optional(),
  colorLogoSvg: Logo.LogoSchema.optional(),
  whiteLogoPng: Logo.LogoSchema.optional(),
  whiteLogoSvg: Logo.LogoSchema.optional(),
});
export const BrandingSchema = z
  .object({
    primaryColor: z
      .string()
      .regex(/^(#([A-Fa-f0-9]{6}))?$/i, "Invalid hex color format")
      .or(z.literal(""))
      .optional(),
  })
  .merge(LogosSchema);

export const FormikSchema = z.object({
  branding: BrandingSchema,
});
type Logos = z.infer<typeof LogosSchema>;
export type FormikType = z.infer<typeof FormikSchema>;
export type Branding = z.infer<typeof BrandingSchema>;
